import { NavLink } from 'react-router-dom';

export interface RouteTab {
  to: string;
  label: string;
  // NavLink's `end`. Required on any tab whose path is a PREFIX of a sibling's, or it stays lit
  // while that sibling is open and two tabs read as active at once.
  end?: boolean;
}

interface RouteTabsProps {
  tabs: RouteTab[];
}

// An underlined tab strip whose active tab is derived from the URL rather than local state.
//
// Extracted from ExamTabs when the admin Analytics screen needed the same thing, and it carries
// that component's reasoning: admin views get sent to colleagues ("look at this referee"), and a
// tab you cannot link to is a recurring annoyance. Deriving from the route also means a deep link
// opens on the right tab and the back button behaves.
export function RouteTabs({ tabs }: RouteTabsProps) {
  return (
    // Scrolls rather than wraps on a narrow viewport: a wrapped tab strip puts a second row of
    // tabs under the underline that's meant to mark the active one, which reads as two separate
    // controls. The hidden scrollbar keeps the strip clean — the partially-cut last tab is the
    // affordance, as in every native tab bar.
    <div
      className="mb-6 flex items-center gap-1 overflow-x-auto border-b border-line [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      role="tablist"
    >
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.end}
          // `replace` so flipping between sibling tabs doesn't stack history entries — going back
          // should leave the section, not step back through every tab visited inside it.
          replace
          role="tab"
          className={({ isActive }) =>
            // flex-none + nowrap: inside the scroller a tab must keep its natural width, or flex
            // would shrink every label to fit and defeat the scroll.
            'relative -mb-px flex-none whitespace-nowrap border-b-2 px-3 py-2.5 text-[13px] font-medium transition-colors sm:px-4 ' +
            (isActive ? 'border-accent text-ink' : 'border-transparent text-muted hover:text-ink-2')
          }
        >
          {tab.label}
        </NavLink>
      ))}
    </div>
  );
}
